import { useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Grid } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { Estate_CreateEstateInput } from 'queries';

import FormDateField from 'common/form-date-field';
import FormSelectField from 'common/form-select-field';
import FormTextField from 'common/form-text-field';

import EstateTrustAdminGrantorForm, { GrantorTrustee } from './estate-trustadmin-grantor-form';
import EstateTrustAdminTrusteeForm from './estate-trustadmin-trustee-form';

const useStyles = makeStyles(() =>
  createStyles({
    formSection: {
      paddingBottom: '10px',
    },
    inputGrid: {
      paddingRight: '20px',
    },

    trusteeContainer: {
      marginTop: '30px',
    },
  }),
);

const EstateDetailTrustadminForm = () => {
  const classes = useStyles();

  const [copyCount, setCopyCount] = useState(1); // Hack to get the trustee form to re-render when a grantor is copied

  const {
    control,
    formState: { errors },
  } = useFormContext<Estate_CreateEstateInput>();

  const { append: appendEstateTrustee } = useFieldArray({
    control,
    name: 'estateTrustadminFields.estateTrustees',
  });

  const copyGrantorToTrustee = (grantor: GrantorTrustee) => {
    appendEstateTrustee(
      {
        trusteeFirstName: grantor.grantorFirstName,
        trusteeLastName: grantor.grantorLastName,
        isPrimaryTrustee: grantor.isPrimaryTrustee,
        sortOrder: grantor.sortOrder,
      },
      { shouldFocus: false },
    );
    setCopyCount(copyCount + 1);
  };

  return (
    <div>
      <div className={classes.formSection}>
        <Grid container style={{ display: 'inline' }}>
          <Grid style={{ display: 'inline' }} className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTrustadminFields.trustName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Trust Name"
                  autoFocus
                  errorMessage={errors.estateTrustadminFields?.trustName?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTrustadminFields.trustDate"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <FormDateField {...{ value, onChange, name }} label="Trust Date" />
              )}
            />
          </Grid>
        </Grid>
        <EstateTrustAdminGrantorForm onCopyGrantorToTrustee={copyGrantorToTrustee} />
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTrustadminFields.isGrantorTrust"
              control={control}
              render={({ field: { ref, value, onChange, ...field } }) => (
                <FormSelectField
                  label="Is it a grantor trust or non-grantor trust?"
                  {...field}
                  value={value === true ? 'yes' : 'no'}
                  onChange={(e) => onChange(e.target.value === 'yes')}
                  inputRef={ref}
                  options={[
                    {
                      label: 'Grantor Trust',
                      value: 'yes',
                    },
                    {
                      label: 'Non-Grantor Trust',
                      value: 'no',
                    },
                  ]}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTrustadminFields.isRevocableTrust"
              control={control}
              render={({ field: { ref, value, onChange, ...field } }) => (
                <FormSelectField
                  label="Is it a revocable trust or irrevocable trust?"
                  {...field}
                  value={value === true ? 'yes' : 'no'}
                  onChange={(e) => onChange(e.target.value === 'yes')}
                  inputRef={ref}
                  options={[
                    {
                      label: 'Revocable Trust',
                      value: 'yes',
                    },
                    {
                      label: 'Irrevocable Trust',
                      value: 'no',
                    },
                  ]}
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTrustadminFields.ein"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="If trust is not using grantor SSN and has a separate EIN, list it here."
                  helperText="If you have not yet obtained an EIN, leave this field blank."
                />
              )}
            />
          </Grid>
        </Grid>

        <EstateTrustAdminTrusteeForm key={copyCount} />
      </div>
    </div>
  );
};

export default EstateDetailTrustadminForm;
